

import { makeStyles } from 'tss-react/mui';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import {
  Box,
  Button,
  ContentTemplate,
  Link,
  MUIDataTable,
  MUIDataTableColumn,
  Pill,
  SectionHeader,
  Typography,
  useDataTable,
} from 'web-library';
import { candidateService } from '../../../../api';
import { CandidateRegisteredExamResponse } from '../../../../api/candidate/exam/model';
import RouteKey from '../../../../routes/key';
import { compare, stringToDefaultDisplay } from '../../../../utils/DateUtils';

const { resit: resitRoute, accessArrangement } = RouteKey.child;
const { summary } = RouteKey.resit.child;
const { root, importantNotes } = RouteKey['access-arrangement'].child;

const useStyles = makeStyles()((theme) =>
({
  '@global': {
    '#resit-listing .MuiTableCell-root': {
      '&[data-colindex="0"]': {
        minWidth: 200,
      },
    },
  },
  tableContainer: {
    marginBottom: theme.spacing(3),
  },
  table: {
    marginBottom: theme.spacing(2),
  },
  viewDetailsButton: {
    color: theme.color.link.linkDefault01,
  },
}));

const ResitListingValue = ({ value }: { value: string }) => <Typography>value</Typography>;

const RegistrationStatus = ({ registrationStatus }: { registrationStatus?: string }) => (
  <Pill
    variant={registrationStatus === 'Registered' ? 'positive' : 'info'}
    label={registrationStatus || ''}
  />
);

const ViewDetails = ({
  data,
  dataIndex,
  rowIndex,
}: {
  data: CandidateRegisteredExamResponse[];
  dataIndex: number;
  rowIndex: number;
}) => {
  const { classes } = useStyles();
  const history = useHistory();

  return (
    <Button
      className={classes.viewDetailsButton}
      id={`button-resitListing-row${rowIndex}`}
      variant="text"
      size="small"
      onClick={() => {
        if (data) {
          history.push(
            `/${resitRoute}/${summary.route}?internalTransactionRef=${data?.[dataIndex].internalTransactionRef}`
          );
        }
      }}
    >
      View details
    </Button>
  );
};

const ResitListing = () => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const history = useHistory();

  const { state, dispatch } = useDataTable();
  const { data, isFetching } = candidateService.exam.candidate.useResitRegisteredExamQuery({});

  const sortFunc = useCallback(
    (a: CandidateRegisteredExamResponse, b: CandidateRegisteredExamResponse) => {
      switch (state.sortColumn) {
        case 'examLevel':
          return compare(a.examLevel, b.examLevel, state.order);
        case 'registrationStatus':
          return compare(a.registrationStatus, b.registrationStatus, state.order);
        case 'dateOfApplication':
          return compare(a.dateOfApplication, b.dateOfApplication, state.order);
        default:
          return compare('', '', state.order);
      }
    },
    [state.order, state.sortColumn]
  );

  // @ts-ignore
  const sortedData = useMemo(() => (data || []).sort(sortFunc), [data, sortFunc]);

  const columns: MUIDataTableColumn[] = [
    {
      name: 'examLevel',
      label: 'Exam Level',
      options: {
        sort: true,
        customBodyRenderLite: (dataIndex: number, rowIndex: number) =>
          ResitListingValue({
            value: data?.[dataIndex].examLevel as string,
          }),
      },
    },
    {
      name: 'dateOfApplication',
      label: 'Date of Application',
      options: {
        sort: true,
        customBodyRenderLite: (dataIndex: number, rowIndex: number) =>
          ResitListingValue({
            value: stringToDefaultDisplay(data?.[dataIndex].dateOfApplication || '', true),
          }),
      },
    },
    {
      name: 'registrationStatus',
      label: 'Status',
      options: {
        sort: true,
        customBodyRenderLite: (dataIndex: number, rowIndex: number) =>
          RegistrationStatus({
            registrationStatus: data?.[dataIndex].registrationStatus,
          }),
      },
    },
    {
      name: 'viewDetail',
      label: ' ',
      options: {
        customBodyRenderLite: (dataIndex: number, rowIndex: number) =>
          ViewDetails({
            data: data as CandidateRegisteredExamResponse[],
            dataIndex,
            rowIndex,
          }),
      },
    },
  ];

  return (
    <ContentTemplate type="full" continuous transparent>
      <Box className={classes.tableContainer}>
        <SectionHeader title={t('resit->>ResitListing.All Registration Applications')} />
        <Box className={classes.table} id="resit-listing">
          <MUIDataTable
            state={state}
            dispatch={dispatch}
            isLoading={isFetching}
            columns={columns}
            data={sortedData}
            options={{
              sort: true,
            }}
          />
        </Box>
        <Typography variant="caption">{t('resit->>ResitListing.AccessArrangment')}</Typography>
        <Link
          onClick={() =>
            history.push(`/${accessArrangement}/${root.route}/${importantNotes.route}`)
          }
        >
          <Typography variant="caption">
            {t('resit->>ResitListing.AccessArrangmentLink')}
          </Typography>
        </Link>
      </Box>
    </ContentTemplate>
  );
};

export default ResitListing;
